import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import {
  IonRadioGroup,
  RadioGroupChangeEventDetail,
} from '@ionic/angular/standalone';
import { Subject } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import { VALIDATE_ADDRESS_RESPONSES } from 'src/app/components/shared/shared-constants';
import { nonCanadianPostalValidator } from 'src/app/core/custom-validators/non-canadian-postal/non-canadian-postal.validator';
import { SpecialCharacters } from 'src/app/core/enums';
import {
  CurrentPickupAddress,
  OnDemandPickupAddress,
} from 'src/app/core/models/operations/on-demand-pickup-address.model';
import { ValidateAddressRequest } from 'src/app/core/models/operations/operations-pickup.model';
import { PickupAssignmentsService } from 'src/app/core/services/operations/pickup-assignments/pickup-assignments.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CURRENT_PICKUP_CONTROLS } from '../on-demand-pickup.constants';
import { CurrentPickupForm } from '../on-demand-pickup.model';
import {
  ADDRESS_TYPE,
  PICKUP_ADDRESS_CONSTANTS,
} from './current-pickup-section.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-current-pickup-section',
  templateUrl: './current-pickup-section.component.html',
  styleUrls: ['./current-pickup-section.component.scss'],
  standalone: false,
})
export class CurrentPickupSectionComponent implements OnInit {
  @ViewChild('radioGroup') radioGroup!: IonRadioGroup;

  @Input({ required: true }) currentPickupAddressData!: CurrentPickupAddress;
  @Input({ required: true }) parsedAAAAddressData!: OnDemandPickupAddress;
  @Input({ required: true }) originalAddressData!: OnDemandPickupAddress;
  @Input({ required: true }) form!: FormGroup;
  @Input({ required: true }) orderNumber = '';
  @Input({ required: true }) countryCode = '';
  @Output() addressValidated = new EventEmitter();

  destroy = inject(DestroyRef);
  private userService = inject(UserService);

  valueChanged: Subject<void> = new Subject<void>();
  addressType = ADDRESS_TYPE.ORIGINAL;
  activeAddress = '';
  validated = false;
  validationCode = '';
  validatedLatNr = '';
  validatedLngNr = '';

  private suppressNextChange = false;
  readonly CONSTS = {
    ...PICKUP_ADDRESS_CONSTANTS,
    ...VALIDATE_ADDRESS_RESPONSES,
  };
  readonly CONTROLS = CURRENT_PICKUP_CONTROLS;
  readonly REGEX_LATLONG = SpecialCharacters.LAT_LONG;
  readonly CANADIAN_POSTAL = SpecialCharacters.CANADIAN_POSTAL;

  constructor(
    private readonly fb: NonNullableFormBuilder,
    private readonly pickupAssignmentsService: PickupAssignmentsService,
  ) {}

  ngOnInit(): void {
    this.subscribeToAddressValidationReset();
  }

  fillAddress(address: OnDemandPickupAddress) {
    this.form.get(this.CONTROLS.CUSTOMER_NAME)?.setValue(address.customerName);
    this.form.get(this.CONTROLS.CONTACT_NAME)?.setValue(address.contactName);
    this.form.get(this.CONTROLS.CONTACT)?.setValue(address.contact);
    this.form
      .get(this.CONTROLS.STREET_ADDRESS)
      ?.setValue(address.streetAddress);
    this.form.get(this.CONTROLS.STREET_NAME)?.setValue(address.streetName);
    this.form.get(this.CONTROLS.STREET_TYPE)?.setValue(address.streetType);

    this.form.get(this.CONTROLS.SUITE)?.setValue(address.suite);
    this.form.get(this.CONTROLS.CITY_TOWN)?.setValue(address.cityTown);
    this.form.get(this.CONTROLS.PROVINCE)?.setValue(address.province);
    this.form.get(this.CONTROLS.POSTAL_CODE)?.setValue(address.postalCode);
    this.form.get(this.CONTROLS.POSTAL_CODE)?.updateValueAndValidity();

    this.form
      .get(this.CONTROLS.LATITUDE)
      ?.setValue(address.latLong?.split(',')[0]);
    this.form
      .get(this.CONTROLS.LONGITUDE)
      ?.setValue(address.latLong?.split(',')[1]);
    this.form.get(this.CONTROLS.SUITE)?.setValue(address.suite);
  }

  onItemChange(event: CustomEvent<RadioGroupChangeEventDetail>) {
    switch (event?.detail?.value) {
      case ADDRESS_TYPE.ORIGINAL:
        this.fillAddress(this.originalAddressData);
        break;
      case ADDRESS_TYPE.PARSED:
        this.fillAddress(this.parsedAAAAddressData);
        break;
      default:
        this.initiateForm();
        break;
    }
  }

  private subscribeToAddressValidationReset(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(() => {
        // Ignore changes caused by validate()
        if (this.suppressNextChange) return;

        // If previously validated and user truly changed something
        if (this.validated) {
          this.validated = false;
          this.addressValidated.emit(false);
        }
      });
  }

  validate(): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),
        mergeMap((user) => {
          this.suppressNextChange = true;
          this.addressValidated.emit(true);
          this.validated = true;

          const validateAddressRequest: ValidateAddressRequest = {
            addressValidation: {
              address: {
                consignee: this.form.get(this.CONTROLS.CUSTOMER_NAME)?.value,
                city: this.form.get(this.CONTROLS.CITY_TOWN)?.value,
                state: this.form.get(this.CONTROLS.PROVINCE)?.value,
                postalCode: this.form.get(this.CONTROLS.POSTAL_CODE)?.value,
                country: this.countryCode,
                floor: this.form.get(this.CONTROLS.FLOOR)?.value,
                suite: this.form.get(this.CONTROLS.SUITE)?.value,
                streetParsed: {
                  number: this.form.get(this.CONTROLS.STREET_ADDRESS)?.value,
                  name: this.form.get(this.CONTROLS.STREET_NAME)?.value,
                  prefix: this.form.get(this.CONTROLS.PREFIX)?.value,
                  suffix: this.form.get(this.CONTROLS.SUFFIX)?.value,
                  type: this.form.get(this.CONTROLS.STREET_TYPE)?.value,
                },
              },
              userId: user.loginId,
              orderNumber: this.orderNumber,
            },
          };
          return this.pickupAssignmentsService
            .validateAddress(validateAddressRequest, user?.location)
            .pipe(
              take(1),
              map((data) => {
                this.validationCode = data.status;
                this.form.markAsPristine();

                if (data.status === '0' && data.address) {
                  this.validatedLatNr = data.address.latNr;
                  this.validatedLngNr = data.address.lngNr;
                  this.form
                    .get(this.CONTROLS.LATITUDE)
                    ?.setValue(this.validatedLatNr);

                  this.form
                    .get(this.CONTROLS.LONGITUDE)
                    ?.setValue(this.validatedLngNr);
                }

                if (
                  this.validationCode !== '0' ||
                  (this.validationCode === '0' &&
                    !this.validatedLngNr &&
                    !this.validatedLatNr)
                ) {
                  this.form.setErrors({ invalid: true });
                }

                if (
                  this.validationCode === '0' &&
                  this.validatedLngNr &&
                  this.validatedLatNr
                ) {
                  //Success, so mark all fields as touched and valid
                  this.form.setErrors({ invalid: null });
                  this.form.markAllAsTouched();
                  this.form.updateValueAndValidity();
                }

                setTimeout(() => (this.suppressNextChange = false), 0);
              }),
            );
        }),
      )
      .subscribe();
  }

  private initiateForm() {
    const data = this.currentPickupAddressData;
    this.form = this.fb.group<CurrentPickupForm>({
      customerName: this.fb.control(data.customerName, Validators.required),
      contactName: this.fb.control(data.contactName),
      contact: this.fb.control(data.contact),
      streetAddress: this.fb.control(data.streetAddress),
      prefix: this.fb.control(data.prefix),
      streetName: this.fb.control(data.streetName, Validators.required),
      suffix: this.fb.control(data.suffix),
      streetType: this.fb.control(data.streetType),
      suite: this.fb.control(data.suite),
      floor: this.fb.control(data.floor),
      cityTown: this.fb.control(data.cityTown),
      province: this.fb.control(data.province, Validators.required),
      postalCode: this.fb.control(
        data.postalCode,
        Validators.compose([
          Validators.required,
          nonCanadianPostalValidator(this.countryCode),
        ]),
      ),
      latitude: this.fb.control(data.latitude),
      longitude: this.fb.control(data.longitude),
    });
  }
}
